import React, { FC, MouseEvent, ReactNode, useContext, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { T } from 'src/shared/helpers';
import { DropdownItemBody, DropdownItemText, DropdownListItem, DropdownToneContext } from '../../../../widgets/dropdown';

// RIMUOVERE CHIEDE DUE VOLTE, SENZA FINESTRA (prototipo V3, AGGIORNAMENTI §7; utente, 2026-10-20).
//
// Il bottone o la voce di menu diventa «Are you sure?» e tiene il cestino; il secondo clic rimuove. Un clic
// altrove, Esc o quattro secondi annullano. Previene l'errore senza interrompere: la finestra di conferma
// copriva proprio la cosa che si stava togliendo, e chiedeva di spostare l'occhio e il puntatore altrove.
//
// LE FUNZIONI CHE RIMUOVONO SANNO GIÀ CHIEDERE con una finestra, e la tengono per chi arriva da un'altra
// strada. Chi ha già chiesto qui le chiama dentro `runConfirmed`: loro lo vedono con `isConfirmedRun` e
// rimuovono subito. È una bandiera che vive solo per la durata della chiamata, perché la domanda è stata
// fatta per quel clic e per nessun altro.

const CONFIRM_TIMEOUT = 4000;

let confirmedRun = false;

/** Esegue `fn` sapendo che la conferma è già stata data, con il secondo clic. */
export const runConfirmed = (fn: () => void) => {
    confirmedRun = true;
    try {
        fn();
    } finally {
        confirmedRun = false;
    }
};

/** Vero mentre gira una rimozione già confermata: chi chiederebbe con una finestra, salta la domanda. */
export const isConfirmedRun = () => confirmedRun;

export const confirmLabel = () => T._('Are you sure?', 'Admin');

// LA DOMANDA SI SENTE (utente, 2026-10-22, accessibilità): una regione «status» sola, nascosta alla vista, dice
// che il comando aspetta la conferma e, se nessuno conferma, che è stato annullato. Prima cambiava solo la scritta.
let liveRegion: HTMLElement | null = null;
const announce = (text: string) => {
    if (typeof document === 'undefined') return;
    if (!liveRegion || !document.body.contains(liveRegion)) {
        liveRegion = document.createElement('div');
        liveRegion.setAttribute('role', 'status');
        liveRegion.setAttribute('aria-live', 'polite');
        liveRegion.style.cssText = 'position:absolute;width:1px;height:1px;overflow:hidden;clip-path:inset(50%);white-space:nowrap;';
        document.body.appendChild(liveRegion);
    }
    // Svuotata e riscritta, così la stessa frase detta due volte si sente due volte.
    liveRegion.textContent = '';
    window.setTimeout(() => liveRegion && (liveRegion.textContent = text), 50);
};

const isKeyboardFocus = (element: Element) => {
    try {
        return element.matches(':focus-visible');
    } catch {
        return false;
    }
};

/**
 * Il primo clic arma e si ferma lì (anche il menu che lo contiene resta aperto); il secondo esegue.
 * Lo stato armato si spegne da solo con Esc, con un clic fuori o dopo quattro secondi.
 */
export const useConfirmTwice = (onConfirm: () => void) => {
    const [armed, setArmed] = useState(false);
    // Chi ha chiesto: un clic fuori di lui annulla. Si prende dal clic, così nessun bottone deve passare un ref.
    const target = useRef<Element | null>(null);

    useEffect(() => {
        if (!armed) return;
        const cancel = () => {
            setArmed(false);
            announce(T._('Removal cancelled.', 'Admin'));
        };
        announce(T._('Are you sure? Press again to remove.', 'Admin'));
        // IL TEMPO NON SCADE MENTRE CI SEI SOPRA COL FUOCO DA TASTIERA (WCAG 2.2.1): chi usa la tastiera o uno screen
        // reader ha bisogno di più dei quattro secondi, e il conto riparte finché il fuoco resta lì. Il clic del mouse
        // lascia anche lui il fuoco sul bottone (Chrome), ma non :focus-visible: col mouse scade dopo quattro secondi, come prima.
        let timer = 0;
        const expire = () => {
            const active = document.activeElement;
            if (target.current && active && target.current.contains(active) && isKeyboardFocus(active)) {
                timer = window.setTimeout(expire, CONFIRM_TIMEOUT);
                return;
            }
            cancel();
        };
        timer = window.setTimeout(expire, CONFIRM_TIMEOUT);
        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') cancel();
        };
        const onPointer = (event: PointerEvent) => {
            if (target.current && !target.current.contains(event.target as Node)) cancel();
        };
        const onFocusOut = (event: FocusEvent) => {
            if (target.current && target.current.contains(event.target as Node) && !target.current.contains(event.relatedTarget as Node | null)) cancel();
        };
        document.addEventListener('keydown', onKey, true);
        document.addEventListener('pointerdown', onPointer, true);
        document.addEventListener('focusout', onFocusOut, true);
        return () => {
            window.clearTimeout(timer);
            document.removeEventListener('keydown', onKey, true);
            document.removeEventListener('pointerdown', onPointer, true);
            document.removeEventListener('focusout', onFocusOut, true);
        };
    }, [armed]);

    const onClick = (event: MouseEvent) => {
        if (!armed) {
            event.preventDefault();
            event.stopPropagation();
            target.current = event.currentTarget;
            setArmed(true);
            return;
        }
        setArmed(false);
        runConfirmed(onConfirm);
    };

    return { armed, onClick, 'data-confirming': armed ? '' : undefined };
};

/** Per chi disegna da sé il proprio bottone: riceve lo stato e le props da mettergli. */
export const ConfirmTwice: FC<{ onConfirm: () => void; children: (confirm: ReturnType<typeof useConfirmTwice>) => ReactNode }> = ({ onConfirm, children }) => (
    <>{children(useConfirmTwice(onConfirm))}</>
);

// L'ASPETTO DI CHI CHIEDE: un fondo rosso chiaro e la parola rossa, regolare. Si scrive una volta sul
// contenitore degli editor (`editorPalette`), così vale per ogni bottone che porta `data-confirming`.
export const confirmingStyle = css`
    [data-confirming][data-confirming][data-confirming] {
        width: auto;
        min-width: 0;
        gap: 4px;
        padding-inline: 8px;
        border-color: transparent;
        background: var(--editor-confirm-bg, #fce4e4);
        box-shadow: none;
        color: var(--editor-danger, #c33a31);
        font-weight: 400;
        opacity: 1;

        &:hover {
            background: var(--editor-confirm-bg-hover, #f6d2d1);
            color: var(--editor-danger, #c33a31);
        }
    }
`;

export const ConfirmText = styled.span`
    white-space: nowrap;
    font-size: 12px;
    font-weight: 400;
`;

// LA VOCE DI MENU che chiede: il menu vive fuori dal contenitore dell'editor, quindi porta il suo aspetto.
const ConfirmListItem = styled(DropdownListItem)`
    &[data-confirming] {
        background: var(--editor-confirm-bg, #fce4e4);
        color: var(--editor-danger, #c33a31);

        &:hover {
            background: var(--editor-confirm-bg-hover, #f6d2d1);
            color: var(--editor-danger, #c33a31);
        }
    }
`;

/** La voce «Remove» di un menu, che chiede due volte. Tiene il suo glifo: cambia solo la parola. */
export const ConfirmDropdownItem: FC<{ icon?: ReactNode; children: ReactNode; onConfirm: () => void; disabled?: boolean }> = ({
    icon,
    children,
    onConfirm,
    disabled
}) => {
    const confirm = useConfirmTwice(onConfirm);
    const soft = useContext(DropdownToneContext) === 'soft';

    return (
        <ConfirmListItem
            type='button'
            role='menuitem'
            $danger
            $soft={soft}
            disabled={disabled}
            onClick={confirm.onClick}
            data-confirming={confirm['data-confirming']}
        >
            {icon}
            <DropdownItemBody>
                <DropdownItemText>{confirm.armed ? confirmLabel() : children}</DropdownItemText>
            </DropdownItemBody>
        </ConfirmListItem>
    );
};
